import React, { useContext } from 'react';
import classNames from 'classnames';

import type { StepperHeaderProps } from './types';

import { Icon } from 'app/design-system/components/Icon';
import { Hint } from 'app/design-system/components/Popper';

import { StepperContext } from '../context';

import styles from './Header.module.scss';

export const MINIMUM_STEPS = 2;
export const MAXIMUM_STEPS = 8;

export const Header: React.FC<StepperHeaderProps> = ({
  totalSteps,
  hint,
  className,
  stepsLabels,
}) => {
  const { activeStep } = useContext(StepperContext);
  const _classNames = classNames(styles.stepsList, className);

  if (totalSteps < MINIMUM_STEPS) {
    totalSteps = MINIMUM_STEPS;
  }

  if (totalSteps > MAXIMUM_STEPS) {
    totalSteps = MAXIMUM_STEPS;
  }

  return (
    <ul className={_classNames}>
      {Array.from({ length: totalSteps }, (_elem, index) => {
        const stepIndex = index + 1;

        const _classNames = classNames(styles.stepsListItemContent, {
          [styles.stepsListItemActive]: stepIndex === activeStep,
          [styles.stepsListItemCompleted]: stepIndex < activeStep,
        });

        const title = getStepTitle(activeStep, stepIndex, stepsLabels);

        if (!hint) {
          return (
            <li key={stepIndex}
                title={`${title} ${stepIndex}`}
                className={styles.stepsListItem}>
              <p className={_classNames}
                 data-testid={`step:${stepIndex}`}>
                {stepIndex < activeStep
                  ? (
                    <Icon icon="check"
                          size={8}
                          className={styles.icon} />
                  )
                  : stepIndex}
              </p>
            </li>
          );
        }

        return (
          <li key={stepIndex}
              className={styles.stepsListItem}>
            <Hint content={hint[stepIndex]}
                  childrenWrapperTag="div"
                  placement="top">
              <p className={_classNames}
                 data-testid={`step:${stepIndex}`}>
                {stepIndex < activeStep
                  ? (
                    <Icon icon="check"
                          size={8}
                          className={styles.icon} />
                  )
                  : stepIndex}
              </p>
            </Hint>
          </li>
        );
      })}
    </ul>
  );
};

function getStepTitle(
  activeStep: number,
  stepIndex: number,
  stepsLabels: StepperHeaderProps['stepsLabels'],
) {
  if (!stepsLabels) {
    return stepIndex === activeStep
      ? activeStep
      : stepIndex;
  }

  if (stepIndex === activeStep) {
    return stepsLabels.active;
  }

  return stepIndex < activeStep
    ? stepsLabels.visited
    : stepsLabels.notVisited;
}
